import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { Button, SpinningLoader, TextInput } from "risa-oasis-ui_v2";
import {
  NarPdfConfigService,
  PdfConfigData,
  PdfPageConfigUpdate,
} from "../../../../../../shared/api/services/narPdfConfigService";

interface PdfConfigDisplayProps {
  pdfConfigData: PdfConfigData | null;
  pdfLoading: boolean;
  pdfError: string | null;
  payerId: string;
  subcollection: string;
  onConfigUpdate?: () => void;
  onRetry?: () => void;
}

const PdfConfigDisplay = ({
  pdfConfigData,
  pdfLoading,
  pdfError,
  payerId,
  subcollection,
  onConfigUpdate,
  onRetry,
}: PdfConfigDisplayProps) => {
  const [startPage, setStartPage] = useState<string>("");
  const [endPage, setEndPage] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form values when data loads
  useEffect(() => {
    if (pdfConfigData?.pdf_config) {
      setStartPage(pdfConfigData.pdf_config.start_page?.toString() ?? "");
      setEndPage(pdfConfigData.pdf_config.end_page?.toString() ?? "");
    }
  }, [pdfConfigData]);

  const handleSave = async () => {
    if (!payerId || !subcollection) {
      toast.error("Payer ID and subcollection are required");
      return;
    }

    setIsSaving(true);

    try {
      const pageUpdate: PdfPageConfigUpdate = {};
      if (startPage !== "") {
        pageUpdate.startPage = parseInt(startPage, 10);
      }
      if (endPage !== "") {
        pageUpdate.endPage = parseInt(endPage, 10);
      }

      await NarPdfConfigService.updatePdfPageConfig(
        payerId,
        subcollection,
        pageUpdate,
      );

      toast.success("PDF config saved successfully!");
      if (onConfigUpdate) {
        onConfigUpdate();
      }
    } catch (error) {
      console.error("Error saving PDF config:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save PDF config",
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (pdfLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <SpinningLoader />
      </div>
    );
  }

  if (pdfError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-red-700">{pdfError}</p>
          {onRetry && (
            <Button
              buttonType="primary"
              size="small"
              onClick={onRetry}
              disabled={pdfLoading}
            >
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!pdfConfigData) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-gray-600">No data available</p>
          <p className="text-xs text-gray-500">
            PDF configuration data not found for this payer and plan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto p-2">
      <div className="flex flex-col gap-6 p-4">
        {/* PDF Config Section */}
        <div className="rounded-lg border border-primaryGray-14 p-4">
          <div className="mb-3 text-sm font-semibold text-primaryGray-1">
            Page Configuration
          </div>
          <div className="flex flex-col gap-4">
            <TextInput
              key={`start-page-${payerId}-${subcollection}`}
              id="start-page"
              label="Start Page"
              type="number"
              placeholder="Enter start page number"
              defaultValue={startPage}
              onChange={(e) => setStartPage(e.value)}
            />
            <TextInput
              key={`end-page-${payerId}-${subcollection}`}
              id="end-page"
              label="End Page"
              type="number"
              placeholder="Enter end page number"
              defaultValue={endPage}
              onChange={(e) => setEndPage(e.value)}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PdfConfigDisplay;
